import type { ConnectionSessionResponse, SpeechRequest, TemporaryCredentials } from '@/api/types';
import { FiloApiError, type FiloApiClient } from '@/api/FiloApiClient';
import { getEnv } from '@/config/env';
import type { DirectoryEvent, RobotDirectoryClient } from './RobotDirectoryClient';
import type { RobotButtonEvent } from './RobotConnectionSession';
import {
  RobotConnectionSession,
  type RobotConnectionConfig,
  type RobotConnectionSnapshot,
} from './RobotConnectionSession';
import type { RpcCallOptions, RpcService } from './rpcTypes';

export const MAX_ROBOT_CONNECTIONS = 10;
export const MIN_ROBOT_VOLUME = 0;
export const MAX_ROBOT_VOLUME = 15;

export type ConnectionSessionLike = {
  robotId: string;
  subscribe(listener: () => void): () => void;
  subscribeRobotButton(listener: (event: RobotButtonEvent) => void): () => void;
  getSnapshot(): RobotConnectionSnapshot;
  isConnected(): boolean;
  updateConfig(config: RobotConnectionConfig): boolean;
  connect(config?: RobotConnectionConfig): Promise<void>;
  disconnect(reason?: string): Promise<void>;
  dispose(): void;
  call<T = unknown>(service: RpcService, data?: unknown, options?: RpcCallOptions): Promise<T>;
  requestCamera(enabled: boolean, options?: RpcCallOptions): Promise<unknown>;
  subscribeRobotButton(listener: (event: RobotButtonEvent) => void): () => void;
  handleDirectoryEvent(event: DirectoryEvent): boolean;
};

export type MultiRobotSnapshot = {
  sessions: Record<string, RobotConnectionSnapshot>;
  preparationFailures: Record<string, RobotPreparationFailure>;
  representativeRobotId: string | null;
  isConnectionChangeInProgress: boolean;
  isRunInProgress: boolean;
  runTargets: FlowRunTargets | null;
  lastError: string | null;
};

export type RobotPreparationFailure = {
  state: 'offline' | 'failed';
  lastError: string;
};

export type FlowRunTargets = {
  readonly runId: string;
  readonly robotIds: readonly string[];
  readonly generations: Readonly<Record<string, number>>;
};
type Listener = () => void;
type SessionFactory = (robotId: string) => ConnectionSessionLike;

export class MultiRobotConnectionManager {
  private sessions = new Map<string, ConnectionSessionLike>();
  private sessionUnsubscribes = new Map<string, () => void>();
  private backendSessionIds = new Set<string>();
  private backendSessions = new Map<string, { expiresAt: string; robotIds: string[] }>();
  private refreshTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private cameraLeases = new Map<string, { robotId: string }>();
  private cameraActiveRobots = new Set<string>();
  private cameraUnknownRobots = new Set<string>();
  private cameraOperationQueues = new Map<string, Promise<void>>();
  private cameraMigrationQueues = new Map<string, Promise<boolean>>();
  private listeners = new Set<Listener>();
  private readonly sessionFactory: SessionFactory;
  private directoryGeneration = 0;
  private lifecycleGeneration = 0;
  private disposed = false;
  private snapshot: MultiRobotSnapshot = {
    sessions: {},
    preparationFailures: {},
    representativeRobotId: null,
    isConnectionChangeInProgress: false,
    isRunInProgress: false,
    runTargets: null,
    lastError: null,
  };

  constructor(
    private readonly api: FiloApiClient,
    private readonly directory: RobotDirectoryClient,
    sessionFactory?: SessionFactory,
  ) {
    this.sessionFactory = sessionFactory ?? ((robotId) => new RobotConnectionSession(
      robotId,
      (event) => api.reportTelemetry(event),
      (current) => this.prepareReconnectConfig(current),
    ));
  }

  subscribe(listener: Listener) {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  getSnapshot = () => this.snapshot;

  synthesizeSpeech = (data: SpeechRequest, signal?: AbortSignal) => this.api.synthesizeSpeech(data, signal);

  getSession(robotId: string) {
    return this.sessions.get(robotId);
  }

  subscribeRobotButton(robotId: string, listener: (event: RobotButtonEvent) => void) {
    return this.sessions.get(robotId)?.subscribeRobotButton(listener) ?? (() => undefined);
  }

  callRobot<T = unknown>(
    robotId: string,
    service: RpcService,
    data?: unknown,
    options?: RpcCallOptions,
  ): Promise<T> {
    const session = this.sessions.get(robotId);
    if (!session?.isConnected()) throw new Error(`Robot ${robotId} is not connected`);
    return session.call<T>(service, data, options);
  }

  getConnectedRobotIds(): string[] {
    return [...this.sessions.values()].filter((session) => session.isConnected()).map((session) => session.robotId).sort();
  }

  async changeVolume(level: number): Promise<number> {
    if (!Number.isInteger(level) || level < MIN_ROBOT_VOLUME || level > MAX_ROBOT_VOLUME) {
      throw new Error(`音量は${MIN_ROBOT_VOLUME}から${MAX_ROBOT_VOLUME}の整数で指定してください`);
    }
    const robotIds = this.getConnectedRobotIds();
    if (robotIds.length === 0) throw new Error('接続済みのロボットが0台のため、音量を変更できません');
    const results = await Promise.allSettled(robotIds.map((robotId) => (
      this.sessions.get(robotId)!.call('changeVolume', level)
    )));
    const failedRobotIds = results.flatMap((result, index) => (
      result.status === 'rejected' ? [robotIds[index]] : []
    ));
    if (failedRobotIds.length > 0) {
      const successCount = robotIds.length - failedRobotIds.length;
      const prefix = successCount > 0
        ? `音量を${successCount}台に反映しましたが、`
        : '音量を変更できませんでした。';
      throw new Error(`${prefix}${failedRobotIds.join('、')}への反映に失敗しました`);
    }
    return robotIds.length;
  }

  getRunTargetSession(targets: FlowRunTargets, robotId: string): ConnectionSessionLike | undefined {
    const expectedGeneration = targets.generations[robotId];
    const session = this.sessions.get(robotId);
    if (expectedGeneration === undefined || !session?.isConnected()) return undefined;
    return session.getSnapshot().generation === expectedGeneration ? session : undefined;
  }

  getActiveRunRobotIds(targets: FlowRunTargets): string[] {
    return targets.robotIds.filter((robotId) => this.getRunTargetSession(targets, robotId) !== undefined);
  }

  subscribeRunTargets(targets: FlowRunTargets, listener: (activeRobotIds: string[]) => void) {
    let previous: string | null = null;
    const publishActiveTargets = () => {
      const activeRobotIds = this.getActiveRunRobotIds(targets);
      const next = activeRobotIds.join('\u0000');
      if (next === previous) return;
      previous = next;
      listener(activeRobotIds);
    };
    const unsubscribe = this.subscribe(publishActiveTargets);
    publishActiveTargets();
    return unsubscribe;
  }

  async connectRobots(requestedRobotIds: string[]): Promise<ConnectionSessionResponse> {
    const robotIds = [...new Set(requestedRobotIds)];
    if (robotIds.length === 0) throw new Error('接続するロボットを選んでください');
    const newIds = robotIds.filter((id) => !this.sessions.has(id));
    if (this.sessions.size + newIds.length > MAX_ROBOT_CONNECTIONS) throw new Error(`同時に接続できるのは${MAX_ROBOT_CONNECTIONS}台までです`);
    const lifecycleGeneration = this.beginConnectionChange('実行中は接続を変更できません');
    try {
      this.clearPreparationFailures(robotIds);
      this.setError(null);
      const response = await this.api.createConnectionSession(robotIds);
      if (!this.isConnectionChangeCurrent(lifecycleGeneration)) {
        await this.closeUntrackedBackendSession(response.sessionId);
        throw this.connectionChangeEndedError();
      }
      if (response.ready.length > 0 && !response.viewerCredentials) {
        await this.closeUntrackedBackendSession(response.sessionId);
        throw new Error('Viewer認証情報がありません');
      }
      this.applyPreparationResults(response);
      if (response.ready.length === 0) {
        if (response.failed.length) this.setError(response.failed.map((item) => `${item.robotId}: ${item.message}`).join(' / '));
        await Promise.allSettled([this.api.closeConnectionSession(response.sessionId)]);
        return response;
      }
      await this.registerBackendSession(
        response.sessionId,
        response.expiresAt,
        response.ready.map((item) => item.robotId),
      );
      if (!this.isConnectionChangeCurrent(lifecycleGeneration)) {
        await this.closeUntrackedBackendSession(response.sessionId);
        throw this.connectionChangeEndedError();
      }
      this.directoryGeneration += 1;
      const generation = this.directoryGeneration;
      const connectionRequestId = crypto.randomUUID();
      const credentials = response.viewerCredentials;
      const connectTasks = response.ready.map(async (ready) => {
        if (!credentials) return;
        let session = this.sessions.get(ready.robotId);
        if (!session) {
          session = this.sessionFactory(ready.robotId);
          this.sessions.set(ready.robotId, session);
          this.sessionUnsubscribes.set(ready.robotId, session.subscribe(() => this.publish()));
        }
        const config = this.toConnectionConfig(
          ready.robotId,
          ready.channelArn,
          ready.channelName,
          credentials,
          response.sessionId,
          connectionRequestId,
          generation,
        );
        if (!session.updateConfig(config)) await session.connect(config);
      });
      this.publish();
      if (response.ready.length > 0) this.directory.prepare(response.sessionId, response.ready.map((item) => item.robotId), connectionRequestId, generation);
      await Promise.allSettled(connectTasks);
      if (!this.isConnectionChangeCurrent(lifecycleGeneration)) {
        await this.closeUntrackedBackendSession(response.sessionId);
        throw this.connectionChangeEndedError();
      }
      if (!this.snapshot.representativeRobotId) this.setRepresentative(this.getConnectedRobotIds()[0] ?? null);
      if (response.failed.length) this.setError(response.failed.map((item) => `${item.robotId}: ${item.message}`).join(' / '));
      this.scheduleCredentialRefresh(response.sessionId, response.expiresAt);
      return response;
    } finally {
      this.finishConnectionChange(lifecycleGeneration);
    }
  }

  async reconnectRobot(robotId: string) {
    const session = this.sessions.get(robotId);
    if (!session) throw new Error('接続情報がありません');
    const lifecycleGeneration = this.beginConnectionChange('実行中は再接続できません');
    try {
      await session.connect();
    } finally {
      this.finishConnectionChange(lifecycleGeneration);
    }
  }

  async disconnectRobot(robotId: string) {
    const session = this.sessions.get(robotId);
    if (!session) return;
    const lifecycleGeneration = this.beginConnectionChange('実行中は切断できません');
    try {
      await session.disconnect('手動で切断しました');
      if (!this.isConnectionChangeCurrent(lifecycleGeneration)) return;
      session.dispose();
      this.sessions.delete(robotId);
      this.sessionUnsubscribes.get(robotId)?.();
      this.sessionUnsubscribes.delete(robotId);
      this.backendSessions.forEach((backendSession, sessionId) => {
        if (!backendSession.robotIds.includes(robotId)) return;
        this.backendSessions.set(sessionId, {
          ...backendSession,
          robotIds: backendSession.robotIds.filter((id) => id !== robotId),
        });
      });
      if (this.snapshot.representativeRobotId === robotId) this.snapshot = { ...this.snapshot, representativeRobotId: this.getConnectedRobotIds()[0] ?? null };
      this.publish();
      await this.closeUnusedBackendSessions();
      if (!this.isConnectionChangeCurrent(lifecycleGeneration)) return;
    } finally {
      this.finishConnectionChange(lifecycleGeneration);
    }
  }

  setRepresentative(robotId: string | null) {
    if (robotId) {
      const runTargets = this.snapshot.isRunInProgress ? this.snapshot.runTargets : null;
      if (runTargets ? !this.getRunTargetSession(runTargets, robotId) : !this.sessions.has(robotId)) return;
    }
    this.snapshot = { ...this.snapshot, representativeRobotId: robotId };
    this.publish();
  }

  getRepresentativeSession(): ConnectionSessionLike | undefined {
    const eligible = this.getEligibleRepresentativeSessions();
    const selectedId = this.snapshot.representativeRobotId;
    return eligible.find((session) => session.robotId === selectedId) ?? eligible[0];
  }

  getRepresentativeStream() {
    return this.getRepresentativeSession()?.getSnapshot().remoteStream ?? null;
  }

  getRobotStream(robotId: string) {
    return this.sessions.get(robotId)?.getSnapshot().remoteStream ?? null;
  }

  async acquireCamera(robotId: string): Promise<string | null> {
    const session = this.sessions.get(robotId);
    if (!session?.isConnected()) return null;
    const token = crypto.randomUUID();
    this.cameraLeases.set(token, { robotId });
    try {
      await this.queueCameraReconciliation(robotId);
      if (this.disposed) throw new Error('Connection manager is disposed');
      return token;
    } catch (error) {
      this.cameraLeases.delete(token);
      await this.queueCameraReconciliation(robotId).catch(() => undefined);
      throw error;
    }
  }

  async acquireRepresentativeCamera(): Promise<string | null> {
    const session = this.getRepresentativeSession();
    if (!session) return null;
    return this.acquireCamera(session.robotId);
  }

  async releaseCamera(token: string) {
    const lease = this.cameraLeases.get(token);
    if (!lease) return;
    this.cameraLeases.delete(token);
    await this.queueCameraReconciliation(lease.robotId).catch(() => undefined);
  }

  async migrateRepresentativeCamera(token: string): Promise<boolean> {
    const previous = this.cameraMigrationQueues.get(token) ?? Promise.resolve(true);
    const queued = previous
      .catch(() => false)
      .then(() => this.performRepresentativeCameraMigration(token));
    this.cameraMigrationQueues.set(token, queued);
    void queued.finally(() => {
      if (this.cameraMigrationQueues.get(token) === queued) this.cameraMigrationQueues.delete(token);
    }).catch(() => undefined);
    return queued;
  }

  private async performRepresentativeCameraMigration(token: string): Promise<boolean> {
    const lease = this.cameraLeases.get(token);
    if (!lease || this.disposed) return false;
    const nextSession = this.getRepresentativeSession();
    if (!nextSession) {
      await this.releaseCamera(token);
      return false;
    }
    if (nextSession.robotId === lease.robotId) return true;
    const nextLease = { robotId: nextSession.robotId };
    this.cameraLeases.set(token, nextLease);
    try {
      await this.queueCameraReconciliation(nextSession.robotId);
    } catch (error) {
      if (this.cameraLeases.get(token) === nextLease) this.cameraLeases.set(token, lease);
      await Promise.allSettled([
        this.queueCameraReconciliation(nextSession.robotId),
        this.queueCameraReconciliation(lease.robotId),
      ]);
      throw error;
    }
    if (this.cameraLeases.get(token) !== nextLease) {
      await Promise.allSettled([
        this.queueCameraReconciliation(nextSession.robotId),
        this.queueCameraReconciliation(lease.robotId),
      ]);
      return false;
    }
    await this.queueCameraReconciliation(lease.robotId).catch(() => undefined);
    return true;
  }

  handleDirectoryEvent(event: DirectoryEvent) {
    if (!event.robotId) return false;
    return this.sessions.get(event.robotId)?.handleDirectoryEvent(event) ?? false;
  }

  createRunSnapshot(): FlowRunTargets {
    if (this.snapshot.isConnectionChangeInProgress) throw new Error('接続先の変更処理が完了してから実行してください');
    const robotIds = this.getConnectedRobotIds();
    if (robotIds.length === 0) throw new Error('接続済みのロボットが0台のため、実行できません');
    return {
      runId: `run-${crypto.randomUUID()}`,
      robotIds,
      generations: Object.fromEntries(robotIds.map((robotId) => [robotId, this.sessions.get(robotId)!.getSnapshot().generation])),
    };
  }

  setRunInProgress(value: boolean, targets?: FlowRunTargets) {
    if (value && this.snapshot.isConnectionChangeInProgress) throw new Error('接続先の変更処理が完了してから実行してください');
    this.snapshot = {
      ...this.snapshot,
      isRunInProgress: value,
      runTargets: value ? targets ?? this.createRunSnapshot() : null,
    };
    this.publish();
    if (!value) {
      this.backendSessions.forEach((session, sessionId) => {
        if (Date.parse(session.expiresAt) - Date.now() <= 120_000) this.scheduleCredentialRefresh(sessionId, new Date().toISOString());
      });
    }
  }

  async disposeAll() {
    this.disposed = true;
    this.lifecycleGeneration += 1;
    const sessions = [...this.sessions.values()];
    const backendSessionIds = [...this.backendSessionIds];
    this.sessions.clear();
    this.backendSessionIds.clear();
    this.backendSessions.clear();
    this.refreshTimers.forEach((timer) => clearTimeout(timer));
    this.refreshTimers.clear();
    this.cameraLeases.clear();
    this.cameraActiveRobots.clear();
    this.cameraUnknownRobots.clear();
    this.cameraOperationQueues.clear();
    this.cameraMigrationQueues.clear();
    this.sessionUnsubscribes.forEach((unsubscribe) => unsubscribe());
    this.sessionUnsubscribes.clear();
    sessions.forEach((session) => session.dispose());
    this.snapshot = {
      sessions: {},
      preparationFailures: {},
      representativeRobotId: null,
      isConnectionChangeInProgress: false,
      isRunInProgress: false,
      runTargets: null,
      lastError: null,
    };
    this.publish();
    await Promise.allSettled(backendSessionIds.map((id) => this.api.closeConnectionSession(id)));
  }

  private scheduleCredentialRefresh(sessionId: string, expiresAt: string) {
    const existing = this.refreshTimers.get(sessionId);
    if (existing) clearTimeout(existing);
    const refreshAt = Math.max(0, Date.parse(expiresAt) - Date.now() - 120_000);
    const timer = setTimeout(() => {
      this.refreshTimers.delete(sessionId);
      void this.refreshConnectionSession(sessionId);
    }, refreshAt);
    this.refreshTimers.set(sessionId, timer);
  }

  private async refreshConnectionSession(sessionId: string) {
    const trackedSession = this.backendSessions.get(sessionId);
    if (this.disposed || !trackedSession) return;
    if (!trackedSession.robotIds.some((robotId) => this.sessions.has(robotId))) {
      await this.closeBackendSessions([sessionId]);
      return;
    }
    if (this.snapshot.isRunInProgress || this.snapshot.isConnectionChangeInProgress) {
      this.scheduleCredentialRefresh(sessionId, new Date(Date.now() + 150_000).toISOString());
      return;
    }
    if (Date.parse(trackedSession.expiresAt) <= Date.now()) {
      await this.recreateConnectionSession(sessionId);
      return;
    }
    try {
      const response = await this.api.refreshConnectionSession(sessionId);
      const currentTrackedSession = this.backendSessions.get(sessionId);
      if (this.disposed || !currentTrackedSession) return;
      if (this.snapshot.isRunInProgress || this.snapshot.isConnectionChangeInProgress) {
        this.scheduleCredentialRefresh(sessionId, new Date(Date.now() + 150_000).toISOString());
        return;
      }
      const ownedRobotIds = new Set(currentTrackedSession.robotIds.filter((robotId) => this.sessions.has(robotId)));
      if (ownedRobotIds.size === 0) {
        await this.closeBackendSessions([sessionId]);
        return;
      }
      const scopedResponse: ConnectionSessionResponse = {
        ...response,
        ready: response.ready.filter(({ robotId }) => ownedRobotIds.has(robotId)),
        failed: response.failed.filter(({ robotId }) => ownedRobotIds.has(robotId)),
      };
      if (scopedResponse.ready.length > 0 && !scopedResponse.viewerCredentials) throw new Error('更新用Viewer認証情報がありません');
      this.applyPreparationResults(scopedResponse);
      const permanentFailureIds = new Set(
        scopedResponse.failed.filter(({ retryable }) => !retryable).map(({ robotId }) => robotId),
      );
      permanentFailureIds.forEach((robotId) => this.removePermanentlyFailedSession(robotId));
      const retainedRobotIds = [...ownedRobotIds].filter((robotId) => !permanentFailureIds.has(robotId));
      if (retainedRobotIds.length === 0) {
        if (scopedResponse.failed.length) this.setError(scopedResponse.failed.map((item) => `${item.robotId}: ${item.message}`).join(' / '));
        await this.closeBackendSessions([sessionId]);
        return;
      }
      this.backendSessions.set(sessionId, {
        expiresAt: response.expiresAt,
        robotIds: retainedRobotIds,
      });
      this.directoryGeneration += 1;
      const generation = this.directoryGeneration;
      const connectionRequestId = crypto.randomUUID();
      const credentials = scopedResponse.viewerCredentials;
      const configUpdates = scopedResponse.ready.flatMap((ready) => {
        const session = this.sessions.get(ready.robotId);
        if (!session || !credentials) return [];
        const config = this.toConnectionConfig(
          ready.robotId,
          ready.channelArn,
          ready.channelName,
          credentials,
          sessionId,
          connectionRequestId,
          generation,
        );
        return [session.updateConfig(config) ? Promise.resolve() : session.connect(config)];
      });
      if (scopedResponse.ready.length > 0) this.directory.prepare(sessionId, scopedResponse.ready.map((item) => item.robotId), connectionRequestId, generation);
      await Promise.allSettled(configUpdates);
      if (scopedResponse.failed.length) this.setError(scopedResponse.failed.map((item) => `${item.robotId}: ${item.message}`).join(' / '));
      this.scheduleCredentialRefresh(sessionId, response.expiresAt);
    } catch (error) {
      if (this.disposed || !this.backendSessions.has(sessionId)) return;
      if (error instanceof FiloApiError && (error.status === 404 || error.status === 409)) {
        await this.recreateConnectionSession(sessionId);
        return;
      }
      this.setError(error instanceof Error ? error.message : String(error));
      this.scheduleCredentialRefresh(sessionId, new Date(Date.now() + 150_000).toISOString());
    }
  }

  private async recreateConnectionSession(sessionId: string) {
    const trackedSession = this.backendSessions.get(sessionId);
    if (this.disposed || !trackedSession) return;
    if (this.snapshot.isRunInProgress || this.snapshot.isConnectionChangeInProgress) {
      this.scheduleCredentialRefresh(sessionId, new Date(Date.now() + 150_000).toISOString());
      return;
    }
    try {
      const response = await this.connectRobots(trackedSession.robotIds);
      response.failed
        .filter(({ retryable, robotId }) => !retryable && trackedSession.robotIds.includes(robotId))
        .forEach(({ robotId }) => this.removePermanentlyFailedSession(robotId));
      const remaining = this.backendSessions.get(sessionId);
      if (remaining && remaining.robotIds.length === 0) {
        await this.closeBackendSessions([sessionId]);
      } else if (remaining) {
        this.scheduleCredentialRefresh(sessionId, new Date(Date.now() + 150_000).toISOString());
      }
    } catch (error) {
      if (this.disposed || !this.backendSessions.has(sessionId)) return;
      this.setError(error instanceof Error ? error.message : String(error));
      this.scheduleCredentialRefresh(sessionId, new Date(Date.now() + 150_000).toISOString());
    }
  }

  private toConnectionConfig(
    robotId: string,
    channelArn: string,
    channelName: string,
    credentials: TemporaryCredentials,
    connectionSessionId: string,
    connectionRequestId: string,
    directoryGeneration: number,
  ): RobotConnectionConfig {
    return {
      robotId,
      channelArn,
      channelName,
      credentials,
      connectionSessionId,
      connectionRequestId,
      directoryGeneration,
      region: getEnv().awsRegion,
    };
  }

  private prepareReconnectConfig(current: RobotConnectionConfig): RobotConnectionConfig {
    this.directoryGeneration += 1;
    const directoryGeneration = this.directoryGeneration;
    const connectionRequestId = crypto.randomUUID();
    this.directory.prepare(
      current.connectionSessionId,
      [current.robotId],
      connectionRequestId,
      directoryGeneration,
    );
    return { ...current, connectionRequestId, directoryGeneration };
  }

  private queueCameraReconciliation(robotId: string): Promise<void> {
    const previous = this.cameraOperationQueues.get(robotId) ?? Promise.resolve();
    const operation = previous
      .catch(() => undefined)
      .then(() => this.reconcileCameraState(robotId));
    const tail = operation.then(() => undefined, () => undefined);
    this.cameraOperationQueues.set(robotId, tail);
    void tail.finally(() => {
      if (this.cameraOperationQueues.get(robotId) === tail) this.cameraOperationQueues.delete(robotId);
    });
    return operation;
  }

  private async reconcileCameraState(robotId: string) {
    const shouldBeActive = [...this.cameraLeases.values()].some((lease) => lease.robotId === robotId);
    const state = this.cameraUnknownRobots.has(robotId)
      ? 'unknown'
      : this.cameraActiveRobots.has(robotId) ? 'on' : 'off';
    if ((shouldBeActive && state === 'on') || (!shouldBeActive && state === 'off')) return;
    const session = this.sessions.get(robotId);
    if (shouldBeActive) {
      if (!session?.isConnected()) throw new Error(`Robot ${robotId} is not connected`);
      this.cameraUnknownRobots.add(robotId);
      try {
        await session.requestCamera(true, { timeoutMs: 5000 });
        this.cameraActiveRobots.add(robotId);
        this.cameraUnknownRobots.delete(robotId);
      } catch (error) {
        this.cameraUnknownRobots.add(robotId);
        throw error;
      }
      return;
    }
    if (!session?.isConnected()) {
      this.cameraActiveRobots.delete(robotId);
      this.cameraUnknownRobots.delete(robotId);
      return;
    }
    this.cameraUnknownRobots.add(robotId);
    try {
      await session.requestCamera(false, { timeoutMs: 5000 });
      this.cameraActiveRobots.delete(robotId);
      this.cameraUnknownRobots.delete(robotId);
    } catch (error) {
      this.cameraUnknownRobots.add(robotId);
      throw error;
    }
  }

  private async closeUnusedBackendSessions() {
    const unusedSessionIds = [...this.backendSessions.entries()]
      .filter(([, backendSession]) => !backendSession.robotIds.some((robotId) => this.sessions.has(robotId)))
      .map(([sessionId]) => sessionId);
    await this.closeBackendSessions(unusedSessionIds);
  }

  private removePermanentlyFailedSession(robotId: string) {
    const session = this.sessions.get(robotId);
    session?.dispose();
    this.sessions.delete(robotId);
    this.sessionUnsubscribes.get(robotId)?.();
    this.sessionUnsubscribes.delete(robotId);
    this.backendSessions.forEach((backendSession, backendSessionId) => {
      if (!backendSession.robotIds.includes(robotId)) return;
      this.backendSessions.set(backendSessionId, {
        ...backendSession,
        robotIds: backendSession.robotIds.filter((id) => id !== robotId),
      });
    });
    [...this.cameraLeases.entries()].forEach(([token, lease]) => {
      if (lease.robotId === robotId) this.cameraLeases.delete(token);
    });
    void this.queueCameraReconciliation(robotId).catch(() => undefined);
    this.publish();
  }

  private async registerBackendSession(sessionId: string, expiresAt: string, robotIds: string[]) {
    const reassignedRobotIds = new Set(robotIds);
    const emptiedSessionIds: string[] = [];
    this.backendSessions.forEach((backendSession, existingSessionId) => {
      if (existingSessionId === sessionId) return;
      const remainingRobotIds = backendSession.robotIds.filter((robotId) => !reassignedRobotIds.has(robotId));
      if (remainingRobotIds.length === backendSession.robotIds.length) return;
      if (remainingRobotIds.length === 0) {
        emptiedSessionIds.push(existingSessionId);
      } else {
        this.backendSessions.set(existingSessionId, { ...backendSession, robotIds: remainingRobotIds });
      }
    });
    this.backendSessionIds.add(sessionId);
    this.backendSessions.set(sessionId, { expiresAt, robotIds });
    await this.closeBackendSessions(emptiedSessionIds);
  }

  private async closeBackendSessions(sessionIds: string[]) {
    const uniqueSessionIds = [...new Set(sessionIds)];
    uniqueSessionIds.forEach((sessionId) => {
      const timer = this.refreshTimers.get(sessionId);
      if (timer) clearTimeout(timer);
      this.refreshTimers.delete(sessionId);
      this.backendSessions.delete(sessionId);
      this.backendSessionIds.delete(sessionId);
    });
    await Promise.allSettled(uniqueSessionIds.map((sessionId) => this.api.closeConnectionSession(sessionId)));
  }

  private isLifecycleCurrent(generation: number) {
    return !this.disposed && generation === this.lifecycleGeneration;
  }

  private beginConnectionChange(runErrorMessage: string) {
    if (this.disposed) throw new Error('Connection manager is disposed');
    if (this.snapshot.isRunInProgress) throw new Error(runErrorMessage);
    if (this.snapshot.isConnectionChangeInProgress) throw new Error('別の接続先変更処理が進行中です');
    this.snapshot = { ...this.snapshot, isConnectionChangeInProgress: true };
    this.publish();
    return this.lifecycleGeneration;
  }

  private isConnectionChangeCurrent(generation: number) {
    return this.isLifecycleCurrent(generation)
      && this.snapshot.isConnectionChangeInProgress
      && !this.snapshot.isRunInProgress;
  }

  private finishConnectionChange(generation: number) {
    if (!this.isLifecycleCurrent(generation) || !this.snapshot.isConnectionChangeInProgress) return;
    this.snapshot = { ...this.snapshot, isConnectionChangeInProgress: false };
    this.publish();
  }

  private connectionChangeEndedError() {
    return new Error(this.disposed
      ? 'Connection manager is disposed'
      : 'Connection manager is no longer accepting connection changes');
  }

  private async closeUntrackedBackendSession(sessionId: string) {
    await Promise.allSettled([this.api.closeConnectionSession(sessionId)]);
  }

  private setError(lastError: string | null) {
    this.snapshot = { ...this.snapshot, lastError };
    this.publish();
  }

  private clearPreparationFailures(robotIds: readonly string[]) {
    const preparationFailures = { ...this.snapshot.preparationFailures };
    robotIds.forEach((robotId) => { delete preparationFailures[robotId]; });
    this.snapshot = { ...this.snapshot, preparationFailures };
  }

  private applyPreparationResults(response: ConnectionSessionResponse) {
    const preparationFailures = { ...this.snapshot.preparationFailures };
    response.ready.forEach(({ robotId }) => { delete preparationFailures[robotId]; });
    response.failed.forEach(({ robotId, code, message }) => {
      preparationFailures[robotId] = {
        state: code === 'OFFLINE' ? 'offline' : 'failed',
        lastError: message,
      };
    });
    this.snapshot = { ...this.snapshot, preparationFailures };
  }

  private getEligibleRepresentativeSessions(): ConnectionSessionLike[] {
    const runTargets = this.snapshot.isRunInProgress ? this.snapshot.runTargets : null;
    if (runTargets) {
      return runTargets.robotIds.flatMap((robotId) => {
        const session = this.getRunTargetSession(runTargets, robotId);
        return session ? [session] : [];
      });
    }
    return [...this.sessions.values()].filter((session) => session.isConnected());
  }

  private publish() {
    const eligibleRepresentatives = this.getEligibleRepresentativeSessions();
    const representativeRobotId = eligibleRepresentatives.some((session) => session.robotId === this.snapshot.representativeRobotId)
      ? this.snapshot.representativeRobotId
      : eligibleRepresentatives[0]?.robotId ?? null;
    this.snapshot = {
      ...this.snapshot,
      representativeRobotId,
      sessions: Object.fromEntries([...this.sessions.entries()].map(([id, session]) => [id, session.getSnapshot()])),
    };
    this.listeners.forEach((listener) => listener());
  }
}
